import { useEffect, useRef } from "react";

const hash = (v) => {
  const t = 43758.5453 * Math.sin(v);
  return t - Math.floor(t);
};
const hillHeight = (x, list, base) => {
  let r = base;
  for (const p of list) {
    const d = Math.abs(x - p.c) / p.w;
    if (d < 1) r = Math.max(r, p.h * (1 - d));
  }
  return r;
};
const BACK = [{ c: 0.6, h: 0.28, w: 0.32 }, { c: 0.3, h: 0.12, w: 0.28 }];
const MID = [{ c: 0.92, h: 0.42, w: 0.2 }, { c: 0.7, h: 0.26, w: 0.16 }, { c: 0.5, h: 0.14, w: 0.2 }, { c: 0.15, h: 0.05, w: 0.22 }];
const FRONT = [{ c: 0.99, h: 0.47, w: 0.28 }, { c: 0.8, h: 0.42, w: 0.14 }, { c: 0.63, h: 0.34, w: 0.16 }, { c: 0.45, h: 0.22, w: 0.2 }, { c: 0.25, h: 0.08, w: 0.18 }];

// Grass-covered hills: lighter greens catch the light in the distance, deeper greens shade the foreground slopes.
const HILLS = {
  back: ["#B5DCA4", "#9CD08C", "#84C476"],
  mid: ["#3DAA55", "#1F9C45", "#02963A"],
  front: ["#02963A", "#017729", "#016A25", "#00551D"],
  hot: "#FFF4A3",
  flash: "#E9F7DF",
  sky: "rgba(1,119,41,0.3)",
};
const MEADOW = ["#2FA350", "#34A755", "#3AAB5A", "#41AF5F"];
const SUN_CORE = ["#FFE94D", "#FFDE00"];
const SUN_MID = ["#FFDE00", "#F5D400"];
const SUN_EDGE = ["#EBCB00", "#E0C200"];
const SUN_HOT = "#FFF4A3";
const SUN_FLASH = "#FFFBE0";
const RAY = "#EBCB00";

const isBird = (x, y, flap) => {
  const dx = Math.abs(x - 15);
  if ((x - 15) ** 2 / 22 + (y - 9) ** 2 / 4.5 <= 1) return true;
  if ((x - 19.5) ** 2 / 2.4 + (y - 7) ** 2 / 2 <= 1) return true;
  if (dx > 2 && dx <= 13) {
    const wy = 9 - dx * (0.55 + 0.38 * flap);
    if (Math.abs(y - wy) < 2.2 - 0.12 * dx) return true;
  }
  return (x - 10.5) ** 2 / 7 + (y - 9.5) ** 2 / 1.4 <= 1;
};

export const HeroCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const parent = canvas.parentElement;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf;
    let running = true;
    let visible = true;
    let W = 0, H = 0, cell = 7;
    let dots = [], stars = [], sunDots = [], rays = [], birds = [];
    let time = 40 * Math.random();
    let last = performance.now();
    const start = performance.now();
    const ripples = [];
    let lastRx = -9999, lastRy = -9999;
    const mouse = { x: -9999, y: -9999 };
    let sx = 0, sy = 0, sr = 0, scan = 0;

    const setup = () => {
      const mobile = window.innerWidth < 768;
      cell = mobile ? 8 : 7;
      const amp = mobile ? 0.92 : 1.36;
      const rect = parent.getBoundingClientRect();
      W = Math.max(1, rect.width);
      H = Math.max(1, rect.height);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      sx = W * (mobile ? 0.82 : 0.72);
      sy = H * (mobile ? 0.53 : 0.39);
      sr = mobile ? 0.26 * Math.min(W, H) : Math.min(0.1 * W, 0.2 * H);
      const ridgeAt = (x) => {
        const k = Math.round(200 * x);
        const b = 1 - (0.015 + hillHeight(x, BACK, 0) * amp) * (0.96 + 0.08 * hash(2.17 * k));
        const m = 1 - (0.025 + hillHeight(x, MID, 0) * amp) * (0.95 + 0.1 * hash(3.71 * k));
        const f = 1 - (0.04 + hillHeight(x, FRONT, 0) * amp) * (0.96 + 0.08 * hash(5.13 * k));
        return Math.min(b, m, f);
      };
      const cols = Math.ceil(W / cell);
      const rows = Math.ceil(H / cell);
      const rowStep = 1 / rows;
      dots = []; stars = []; sunDots = []; rays = [];
      for (let ci = 0; ci < cols; ci++) {
        const nx = ci / cols;
        const back = 1 - (0.015 + hillHeight(nx, BACK, 0) * amp) * (0.96 + 0.08 * hash(2.17 * ci));
        const mid = 1 - (0.025 + hillHeight(nx, MID, 0) * amp) * (0.95 + 0.1 * hash(3.71 * ci));
        const front = 1 - (0.04 + hillHeight(nx, FRONT, 0) * amp) * (0.96 + 0.08 * hash(5.13 * ci));
        const ridge = Math.min(back, mid, front);
        for (let ri = 0; ri < rows; ri++) {
          const ny = ri / rows;
          const seed = hash(12.9898 * ci + 78.233 * ri);
          const cx = ci * cell + cell / 2;
          const cy = ri * cell + cell / 2;
          const speed = 0.8 + 1.6 * hash(7.7 * ci + 2.9 * ri);
          if (ny < ridge) {
            const dist = Math.hypot(cx - sx, cy - sy);
            if (dist < sr) {
              const e = dist / sr;
              if (e > 0.85 && seed > 0.86) continue;
              const pal = e < 0.45 ? SUN_CORE : e < 0.75 ? SUN_MID : SUN_EDGE;
              sunDots.push({ x: cx, y: cy, seed, phase: seed * Math.PI * 2, speed, solid: false, r: cell * (0.3 + 0.16 * (1 - e)), base: 0.95 - 0.45 * e, color: pal[(2 * seed) | 0], sun: true, ridge });
            } else if (seed > 0.985) stars.push({ x: ci * cell, y: ri * cell, seed });
            continue;
          }
          let color, alpha, rad, layer, solid = false;
          if (ny >= front) {
            layer = "front";
            const depth = Math.min(1, 0.45 + 1.7 * (ny - front) + 0.35 * Math.pow(nx, 1.4));
            if (nx > 0.76 && depth > 0.88) {
              solid = true;
              color = HILLS.front[3]; alpha = 1; rad = 0.55 * cell;
            } else {
              if (seed > 0.94) continue;
              color = HILLS.front[Math.min(3, Math.floor(4 * depth))]; alpha = 0.85 + 0.15 * depth; rad = cell * (0.3 + 0.18 * depth);
            }
          } else if (ny >= mid) {
            layer = "mid";
            const d = ny - mid;
            const edge = d < 2 * rowStep;
            if (!edge && seed > 0.9) continue;
            color = HILLS.mid[Math.min(2, Math.floor(5 * d))];
            alpha = (edge ? 0.98 : 0.8 + 0.5 * d) * (0.6 + 0.4 * Math.min(1, 2.6 * nx));
            rad = cell * (0.3 + 0.22 * d);
          } else {
            layer = "back";
            const d = ny - back;
            const edge = d < 2 * rowStep;
            if (!edge && seed > 0.82) continue;
            color = HILLS.back[Math.min(2, Math.floor(5 * d))];
            alpha = edge ? 0.92 : 0.65 + 0.4 * d;
            rad = 0.3 * cell;
          }
          if (!solid && layer !== "back" && nx > 0.42) {
            const field = Math.sin(0.021 * cx + 0.013 * cy) * Math.sin(0.009 * cx - 0.017 * cy + 1.7);
            if (field > 0.52) color = MEADOW[Math.min(3, Math.floor((field - 0.52) / 0.12))];
          }
          dots.push({ x: cx, y: cy, seed, phase: seed * Math.PI * 2, speed, solid, r: rad, base: Math.min(1, alpha), color, sun: false, ridge: 2 });
        }
      }
      for (let a = 0; a < 70; a++) {
        const ang = Math.PI * (0.08 + (a / 70) * 0.84);
        const cos = Math.cos(ang), sin = Math.sin(ang);
        for (let k = 0; k < 7; k++) {
          const rr = sr * (1.08 + 0.22 * k);
          const px = sx + cos * rr;
          const py = sy - sin * rr;
          if (py < -10 || px < -10 || px > W + 10) break;
          if (py / H > ridgeAt(Math.min(1, Math.max(0, px / W))) - 0.01) break;
          rays.push({ x: px, y: py, base: 0.2 * (1 - k / 8), ph: hash(3.3 * a + 7.1 * k) * Math.PI * 2, tw: 0.6 + hash(a + 13.7 * k) });
        }
      }
      birds = Array.from({ length: mobile ? 3 : 5 }, (_, n) => ({
        p: 1.3 * Math.random() - 0.15,
        speed: 0.02 + 0.035 * Math.random(),
        yBase: H * (0.08 + 0.24 * Math.random()),
        scale: [4.6, 3.6, 2.8, 4.2, 3][n % 5] * (mobile ? 0.7 : 1),
        ph: Math.random() * Math.PI * 2,
        amp: 12 + 20 * Math.random(),
      }));
    };

    const drawDot = (d, sunOffset) => {
      const off = d.sun ? sunOffset : 0;
      let fade = 1;
      if (d.sun) {
        const k = (d.ridge - (d.y + off) / H) / 0.03;
        if (k <= 0) return;
        fade = Math.min(1, k);
      }
      const tw = 0.86 + 0.14 * Math.sin(time * d.speed + d.phase);
      const o = hash(517.7 * d.seed + Math.floor(1.5 * time));
      const flash = o > 0.972 ? (o - 0.972) / 0.028 : 0;
      const sd = d.x / W - scan;
      const sweep = Math.exp((-sd * sd) / 0.0022);
      let ring = 0, px = 0, py = 0;
      for (const rp of ripples) {
        const dx = d.x - rp.x, dy = d.y + off - rp.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const q = dist - rp.r;
        const s = Math.exp((-q * q) / 900) * rp.life * rp.strength;
        if (s > 0.02) { ring += s; px += (dx / dist) * s * 7; py += (dy / dist) * s * 7; }
      }
      if (ring > 1.4) ring = 1.4;
      const mx = d.x - mouse.x, my = d.y + off - mouse.y;
      const md = mx * mx + my * my;
      let near = 0, halo = 0;
      if (md < 15625) {
        near = 1 - md / 15625; near *= near;
        const dist = Math.sqrt(md) || 1;
        const q = dist - 74;
        halo = Math.exp((-q * q) / 1350);
        px += (mx / dist) * near * 9; py += (my / dist) * near * 9;
      }
      let a = (d.base * tw + 0.5 * flash + 0.12 * sweep + 0.3 * halo + 0.2 * ring) * fade;
      if (a > 1) a = 1;
      ctx.globalAlpha = a;
      let col = d.color;
      if (halo > 0.4 || ring > 0.7) col = d.sun ? SUN_HOT : HILLS.hot;
      else if (flash > 0.5 && !d.solid) col = d.sun ? SUN_FLASH : HILLS.flash;
      ctx.fillStyle = col;
      const grow = 1 + 0.28 * near + 0.55 * halo + 0.4 * ring + 0.45 * flash;
      const X = d.x + px, Y = d.y + off + py;
      if (d.solid) {
        const s = 1.04 * cell * (1 + 0.2 * halo);
        ctx.fillRect(X - s / 2, Y - s / 2, s, s);
      } else {
        ctx.beginPath(); ctx.arc(X, Y, d.r * grow, 0, 6.2832); ctx.fill();
      }
    };

    const drawBirds = (now) => {
      ctx.fillStyle = "#1E2022";
      for (const b of birds) {
        b.p += 0.032 * b.speed;
        if (b.p > 1.2) { b.p = -0.2; b.yBase = H * (0.06 + 0.26 * Math.random()); }
        const bx = b.p * W;
        const by = b.yBase - Math.sin(b.p * Math.PI) * H * 0.07 + Math.sin(5 * b.p + b.ph) * b.amp * 0.5;
        const flap = Math.sin(0.008 * now + b.ph);
        for (let gx = 0; gx < 30; gx++) {
          for (let gy = 0; gy < 16; gy++) {
            if (!isBird(gx, gy, flap)) continue;
            const n = hash(3.1 * gx + 7.7 * gy + b.ph);
            if (n < 0.05) continue;
            ctx.globalAlpha = 0.6 + 0.4 * n;
            ctx.fillRect(bx + (gx - 15) * b.scale, by + (gy - 9) * b.scale, 0.72 * b.scale, 0.72 * b.scale);
          }
        }
      }
    };

    const frame = (now) => {
      ctx.clearRect(0, 0, W, H);
      const t = (now - start) / 15000;
      const n = Math.min(1, Math.max(0, t));
      const sunOffset = (1 - (1 - Math.pow(1 - n, 3))) * H * 0.3 + 6 * Math.sin(0.9 * time);
      let glow = Math.min(1, Math.max(0, 2.4 * (t - 1)));
      glow = glow * glow * (3 - 2 * glow);
      ctx.fillStyle = HILLS.sky;
      for (const s of stars) {
        ctx.globalAlpha = 0.18 + 0.32 * (0.5 + 0.5 * Math.sin(1.4 * time + 14 * s.seed));
        ctx.fillRect(s.x, s.y, 2, 2);
      }
      if (glow > 0.01) {
        const g = ctx.createRadialGradient(sx, sy, 0.3 * sr, sx, sy, 2.4 * sr);
        g.addColorStop(0, `rgba(255,222,0,${0.3 * glow})`);
        g.addColorStop(0.55, `rgba(255,230,60,${0.12 * glow})`);
        g.addColorStop(1, "rgba(255,230,60,0)");
        ctx.globalAlpha = 1; ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = RAY;
        for (const r of rays) {
          ctx.globalAlpha = r.base * glow * (0.55 + 0.45 * Math.sin(1.1 * time * r.tw + r.ph));
          ctx.fillRect(r.x + 3 * Math.sin(0.5 * time + r.ph), r.y, 2.4, 2.4);
        }
      }
      scan = ((0.05 * time) % 1.5) - 0.25;
      for (const d of sunDots) drawDot(d, sunOffset);
      for (const d of dots) drawDot(d, sunOffset);
      drawBirds(now);
      ctx.globalAlpha = 1;
    };

    const loop = (now) => {
      if (!running) return;
      raf = requestAnimationFrame(loop);
      if (!visible || now - last < 30) return;
      last = now;
      time += 0.035;
      for (let i = ripples.length - 1; i >= 0; i--) {
        const r = ripples[i];
        r.r += r.v; r.life -= 0.018 + 0.0011 * r.v;
        if (r.life <= 0) ripples.splice(i, 1);
      }
      frame(now);
    };

    const addRipple = (x, y, strength, v) => {
      ripples.push({ x, y, r: 6, v, life: 1, strength });
      if (ripples.length > 6) ripples.shift();
    };
    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      if (x >= -60 && x <= r.width + 60 && y >= -60 && y <= r.height + 60) {
        mouse.x = x; mouse.y = y;
        const dx = x - lastRx, dy = y - lastRy;
        if (dx * dx + dy * dy > 3600) { lastRx = x; lastRy = y; addRipple(x, y, 0.55, 3.4); }
      } else { mouse.x = -9999; mouse.y = -9999; }
    };
    const onDown = (e) => {
      const r = canvas.getBoundingClientRect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      if (x >= 0 && x <= r.width && y >= 0 && y <= r.height) addRipple(x, y, 1.7, 6.5);
    };
    const io = new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; }, { threshold: 0 });
    io.observe(parent);
    setup();
    if (reduced) frame(start + 20000);
    else {
      raf = requestAnimationFrame(loop);
      window.addEventListener("pointermove", onMove, { passive: true });
      window.addEventListener("pointerdown", onDown, { passive: true });
    }
    const onResize = () => { setup(); if (reduced) frame(start + 20000); };
    window.addEventListener("resize", onResize);
    return () => {
      running = false;
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
    };
  }, []);
  return <canvas ref={ref} data-testid="pixel-sun-canvas" className="absolute inset-0 pointer-events-none" aria-hidden="true" />;
};
